import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, Ban } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ALL_CATS } from "@/i18n/slugs";
import { FAMILIES } from "@/i18n/taxonomy";

const BUDGETS = [["moins-5000", "< 5 000 €"], ["5000-10000", "5 000 - 10 000 €"], ["10000-20000", "10 000 - 20 000 €"], ["20000-50000", "20 000 - 50 000 €"], ["50000+", "> 50 000 €"]];
const TIMELINES = [["urgent", "Urgent (< 1 mois)"], ["1-3-mois", "1 à 3 mois"], ["3-6-mois", "3 à 6 mois"], ["6-12-mois", "6 à 12 mois"], ["flexible", "Flexible"]];
const selectCls = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500";

const FIELDS = ["project_type", "project_description", "budget", "timeline", "start_date", "postal_code", "country", "city", "first_name", "last_name", "email", "phone"];

export const LeadEditDialog = ({ lead, onClose, onSaved, API }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) setForm(Object.fromEntries(FIELDS.map((f) => [f, lead[f] ?? (f === "country" ? "BE" : "")])));
  }, [lead]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    const changes = Object.fromEntries(FIELDS.filter((f) => (form[f] ?? "") !== (lead[f] ?? (f === "country" ? "BE" : ""))).map((f) => [f, form[f]]));
    if (Object.keys(changes).length === 0) { toast.info("Aucune modification"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/leads/${lead.id}`, changes, { withCredentials: true });
      toast.success("Lead mis à jour");
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier le lead <span className="font-mono text-orange-600 text-base">{lead?.lead_number}</span></DialogTitle>
          <DialogDescription>Corrigez la catégorie, la localisation, le budget ou les coordonnées avant l'envoi aux entreprises.</DialogDescription>
        </DialogHeader>
        {lead && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="mb-1.5 block">Catégorie</Label>
              <select value={form.project_type || ""} onChange={set("project_type")} className={selectCls} data-testid="edit-project-type">
                {FAMILIES.map((f) => (
                  <optgroup key={f.key} label={f.fr}>
                    {ALL_CATS.filter((c) => c.family === f.key).map((c) => <option key={c.key} value={c.key}>{c.fr.name}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div><Label className="mb-1.5 block">Budget</Label>
              <select value={form.budget || ""} onChange={set("budget")} className={selectCls} data-testid="edit-budget">{BUDGETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><Label className="mb-1.5 block">Délai</Label>
              <select value={form.timeline || ""} onChange={set("timeline")} className={selectCls} data-testid="edit-timeline">{TIMELINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><Label className="mb-1.5 block">Pays</Label>
              <select value={form.country || "BE"} onChange={set("country")} className={selectCls} data-testid="edit-country"><option value="BE">Belgique</option><option value="LU">Luxembourg</option><option value="FR">France</option></select></div>
            <div><Label className="mb-1.5 block">Code postal</Label><Input value={form.postal_code || ""} onChange={set("postal_code")} data-testid="edit-postal-code" /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Ville</Label><Input value={form.city || ""} onChange={set("city")} data-testid="edit-city" /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Description du projet</Label><Textarea rows={3} value={form.project_description || ""} onChange={set("project_description")} data-testid="edit-description" /></div>
            <div><Label className="mb-1.5 block">Prénom</Label><Input value={form.first_name || ""} onChange={set("first_name")} data-testid="edit-first-name" /></div>
            <div><Label className="mb-1.5 block">Nom</Label><Input value={form.last_name || ""} onChange={set("last_name")} data-testid="edit-last-name" /></div>
            <div><Label className="mb-1.5 block">E-mail</Label><Input type="email" value={form.email || ""} onChange={set("email")} data-testid="edit-email" /></div>
            <div><Label className="mb-1.5 block">Téléphone</Label><Input value={form.phone || ""} onChange={set("phone")} data-testid="edit-phone" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="edit-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const REASONS = [
  ["duplicate", "Demande en double"], ["wrong_contact", "Coordonnées incorrectes (téléphone / e-mail)"], ["incomplete", "Description insuffisante"],
  ["out_of_zone", "Zone non couverte"], ["not_serious", "Demande non sérieuse / test"], ["spam", "Spam"], ["other", "Autre"],
];

export const LeadRejectDialog = ({ lead, onClose, onDone, API }) => {
  const [reason, setReason] = useState("duplicate");
  const [message, setMessage] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (lead) { setReason("duplicate"); setMessage(""); setNotify(true); } }, [lead]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/leads/${lead.id}/reject`, { reason, message, notify_client: notify }, { withCredentials: true });
      toast.success(notify ? "Lead refusé, client prévenu par e-mail" : "Lead refusé et archivé");
      onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors du refus"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="lead-reject-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-red-700">Refuser le lead <span className="font-mono text-base">{lead?.lead_number}</span></DialogTitle>
          <DialogDescription>Le lead est archivé dans la corbeille (30 jours) et ne sera jamais envoyé aux entreprises.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Motif</Label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className={selectCls} data-testid="reject-reason">
              {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <Label className="mb-1.5 block">Message complémentaire pour le client (optionnel)</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ex : votre numéro de téléphone semble incomplet, vous pouvez soumettre une nouvelle demande." data-testid="reject-message" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="reject-notify" />
            Prévenir le client par e-mail{lead?.email ? ` (${lead.email})` : ""}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reject-cancel">Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}Refuser le lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
